import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import * as d3 from 'd3';
import ChartWatermark from '../ChartWatermark/ChartWatermark';
import './AiChartSection.css';

const TIME_PERIODS = [
  { key: '1D', label: '1D', hours: 24 },
  { key: '1W', label: '1W', hours: 168 },
  { key: '1M', label: '1M', hours: 720 },
  { key: 'ALL', label: 'ALL', hours: null },
];

function AiChartSection({ equityCurve, challenge }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [timePeriod, setTimePeriod] = useState('ALL');
  const [hoverData, setHoverData] = useState(null);

  const startBalance = challenge ? challenge.startBalance : 10000;

  // Backend now provides computed fields: equity, usedMargin, freeMargin, trueBalance
  const usedMargin = challenge?.usedMargin || 0;
  const freeMargin = challenge?.freeMargin || (challenge?.currentBalance || startBalance);
  const trueBalance = challenge?.trueBalance || (challenge?.currentBalance || startBalance);
  const equity = challenge?.equity || trueBalance;
  const marginLevel = usedMargin > 0 ? (equity / usedMargin) * 100 : 0;
  const changePercent = startBalance ? ((trueBalance - startBalance) / startBalance * 100) : 0;

  // Filter equity data by time period
  const filteredData = useMemo(() => {
    if (!equityCurve || equityCurve.length === 0) return [];

    const parsed = equityCurve.map(d => ({
      date: new Date(d.timestamp || d.date || d.t),
      balance: d.balance || d.value || d.y,
    })).sort((a, b) => a.date - b.date);

    const period = TIME_PERIODS.find(p => p.key === timePeriod);
    if (!period || !period.hours) return parsed;

    const cutoff = new Date(Date.now() - period.hours * 3600000);
    const filtered = parsed.filter(d => d.date >= cutoff);
    return filtered.length > 0 ? filtered : parsed;
  }, [equityCurve, timePeriod]);

  // Display balance (hover or current trueBalance)
  const displayBalance = hoverData ? hoverData.balance : trueBalance;
  const displayChange = hoverData
    ? ((hoverData.balance - startBalance) / startBalance * 100)
    : changePercent;

  const drawChart = useCallback(() => {
    if (!filteredData || filteredData.length === 0 || !svgRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height || 300;
    const margin = { top: 10, right: 10, bottom: 25, left: 50 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    if (innerW <= 0 || innerH <= 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('width', width).attr('height', height).attr('viewBox', `0 0 ${width} ${height}`);

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Scales
    const xScale = d3.scaleTime()
      .domain(d3.extent(filteredData, d => d.date))
      .range([0, innerW]);

    const yMin = Math.min(d3.min(filteredData, d => d.balance), startBalance) * 0.98;
    const yMax = Math.max(d3.max(filteredData, d => d.balance), startBalance) * 1.02;
    const yScale = d3.scaleLinear()
      .domain([yMin, yMax])
      .range([innerH, 0]);

    // Grid lines (horizontal)
    g.append('g')
      .call(d3.axisLeft(yScale).ticks(5).tickSize(-innerW).tickFormat(''))
      .selectAll('line')
      .attr('stroke', 'rgba(255,255,255,0.04)');
    g.selectAll('.domain').remove();

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(d3.axisBottom(xScale).ticks(5).tickFormat(d3.timeFormat('%b %d')))
      .selectAll('text')
      .attr('fill', '#555')
      .attr('font-size', '10px');

    // Y axis
    g.append('g')
      .call(d3.axisLeft(yScale).ticks(5).tickFormat(d => `$${d3.format(',.0f')(d)}`))
      .selectAll('text')
      .attr('fill', '#555')
      .attr('font-size', '10px');

    g.selectAll('.domain').attr('stroke', 'rgba(255,255,255,0.05)');
    g.selectAll('.tick line').attr('stroke', 'rgba(255,255,255,0.05)');

    // Gradient fill
    const defs = svg.append('defs');
    const gradientId = 'chart-area-gradient';
    const gradient = defs.append('linearGradient')
      .attr('id', gradientId)
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '0%').attr('y2', '100%');
    gradient.append('stop').attr('offset', '0%').attr('stop-color', 'rgba(136, 136, 136, 0.2)');
    gradient.append('stop').attr('offset', '100%').attr('stop-color', 'rgba(136, 136, 136, 0.01)');

    // Area
    const area = d3.area()
      .x(d => xScale(d.date))
      .y0(innerH)
      .y1(d => yScale(d.balance))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(filteredData)
      .attr('fill', `url(#${gradientId})`)
      .attr('d', area);

    // Line
    const line = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.balance))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(filteredData)
      .attr('fill', 'none')
      .attr('stroke', '#888')
      .attr('stroke-width', 1.5)
      .attr('d', line);

    // End dot (white)
    const last = filteredData[filteredData.length - 1];
    g.append('circle')
      .attr('cx', xScale(last.date))
      .attr('cy', yScale(last.balance))
      .attr('r', 4)
      .attr('fill', '#fff')
      .attr('stroke', '#0a0a0a')
      .attr('stroke-width', 2);

    // Hover interaction
    const crosshairLine = g.append('line')
      .attr('class', 'ai-chart-crosshair')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', 'rgba(255,255,255,0.2)')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,4')
      .style('display', 'none');

    const hoverDot = g.append('circle')
      .attr('r', 5)
      .attr('fill', '#fff')
      .attr('stroke', '#888')
      .attr('stroke-width', 2)
      .style('display', 'none');

    const bisector = d3.bisector(d => d.date).left;

    const overlay = g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair');

    overlay.on('mousemove', (event) => {
      const [mx] = d3.pointer(event);
      const x0 = xScale.invert(mx);
      const i = bisector(filteredData, x0, 1);
      const d0 = filteredData[i - 1];
      const d1 = filteredData[i];
      if (!d0) return;
      const d = d1 && (x0 - d0.date > d1.date - x0) ? d1 : d0;

      const cx = xScale(d.date);
      const cy = yScale(d.balance);

      crosshairLine
        .attr('x1', cx).attr('x2', cx)
        .style('display', null);

      hoverDot
        .attr('cx', cx).attr('cy', cy)
        .style('display', null);

      setHoverData({ balance: d.balance, date: d.date });
    });

    overlay.on('mouseleave', () => {
      crosshairLine.style('display', 'none');
      hoverDot.style('display', 'none');
      setHoverData(null);
    });
  }, [filteredData, startBalance]);

  useEffect(() => {
    drawChart();

    const handleResize = () => drawChart();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [drawChart]);

  return (
    <div className="ai-chart-section" data-tour="ai-chart-section">
      {/* Header */}
      <div className="ai-chart-header">
        <div className="ai-chart-header-left">
          <div className="ai-chart-label">Portfolio Performance</div>
          <div className="ai-chart-value">
            <span className="ai-chart-balance">
              ${displayBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className={`ai-chart-change ${displayChange >= 0 ? 'positive' : 'negative'}`}>
              {displayChange >= 0 ? '+' : ''}{displayChange.toFixed(2)}%
            </span>
          </div>
        </div>
        <div className="ai-chart-time-buttons">
          {TIME_PERIODS.map(p => (
            <button
              key={p.key}
              className={`ai-chart-time-btn ${timePeriod === p.key ? 'active' : ''}`}
              onClick={() => setTimePeriod(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      {filteredData.length > 0 ? (
        <div className="ai-chart-container" ref={containerRef}>
          <svg ref={svgRef} />
          <ChartWatermark />
        </div>
      ) : (
        <div className="ai-chart-empty">No equity data yet</div>
      )}

      {/* Footer Stats */}
      <div className="ai-chart-footer">
        <div className="ai-chart-footer-stat">
          <span className="ai-chart-footer-label">Used Margin</span>
          <span className="ai-chart-footer-value">
            ${usedMargin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className="ai-chart-footer-stat">
          <span className="ai-chart-footer-label">Free Margin</span>
          <span className="ai-chart-footer-value">
            ${freeMargin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className="ai-chart-footer-stat">
          <span className="ai-chart-footer-label">Margin Level</span>
          <span className="ai-chart-footer-value">
            {marginLevel > 0 ? `${marginLevel.toFixed(1)}%` : '-'}
          </span>
        </div>
      </div>
    </div>
  );
}

export default AiChartSection;
